(function () {
  const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));

  function init(section) {
    if (section.dataset.routeReady) return;
    section.dataset.routeReady = 'true';

    const path = section.querySelector('[data-route-progress]');
    const basePath = section.querySelector('.product-scroll-route__route-base');
    const revealTargets = [...section.querySelectorAll('[data-route-item], [data-route-range]')];
    const marker = section.querySelector('[data-route-marker]');
    const routeDot = section.querySelector('[data-route-dot]');
    const valueElement = section.querySelector('[data-route-value]');
    const milesElement = section.querySelector('[data-route-miles]');
    const kmElement = section.querySelector('[data-route-km]');
    const rangeNumbers = [...section.querySelectorAll('[data-route-range-value]')];
    const mediaElements = [...section.querySelectorAll('.product-scroll-route__media img, .product-scroll-route__media video')];
    const mobileQuery = window.matchMedia('(max-width: 767px)');
    const desktopPath = path.getAttribute('d');
    const desktopBasePath = basePath ? basePath.getAttribute('d') : desktopPath;
    const startValue = Number(marker.dataset.startValue || 100);
    const endValue = Number(marker.dataset.endValue || 0);
    const zeroPoint = clamp(Number(marker.dataset.zeroPoint || 86) / 100, .7, 1);
    const startMiles = Number(milesElement.dataset.startValue || 0);
    const startKm = Number(kmElement.dataset.startValue || 0);
    const endMiles = Number(milesElement.dataset.endValue || 54.29);
    const endKm = Number(kmElement.dataset.endValue || 67.75);
    let targetProgress = 0;
    let smoothProgress = 0;
    let progressVelocity = 0;
    let frame = 0;
    let previousTime = 0;
    let activeUntil = 0;
    let mapDocumentTop = 0;
    let mapHeight = 1;
    let mapScaleX = 1;
    let mapScaleY = 1;
    let mapOffsetX = 0;
    let mapOffsetY = 0;
    let markerMatrix = null;
    let markerOffsetX = 0;
    let markerOffsetY = 0;
    let viewport = 1;
    let renderedValue = '';
    let renderedMiles = '';
    let renderedKm = '';
    let pathLength = 1;
    let screenPathLength = 1;
    let pathSamples = [];
    let activeRouteMode = '';

    function syncRoutePath() {
      const nextMode = mobileQuery.matches ? 'mobile' : 'desktop';
      if (nextMode === activeRouteMode) return;

      path.setAttribute('d', nextMode === 'mobile' ? path.dataset.mobilePath : desktopPath);
      if (basePath) {
        basePath.setAttribute('d', nextMode === 'mobile' ? basePath.dataset.mobilePath : desktopBasePath);
      }

      pathLength = path.getTotalLength();
      pathSamples = Array.from({ length: 301 }, (_, index) => {
        const progress = index / 300;
        const point = path.getPointAtLength(pathLength * progress);
        return { progress, x: point.x, y: point.y, searchY: point.y, screenLength: 0 };
      });
      for (let index = 1; index < pathSamples.length; index += 1) {
        pathSamples[index].searchY = Math.max(pathSamples[index - 1].searchY, pathSamples[index].searchY);
      }

      path.style.strokeDasharray = `${pathLength} ${pathLength}`;
      path.style.strokeDashoffset = `${pathLength}`;
      if (basePath) {
        basePath.style.strokeDasharray = `${pathLength * zeroPoint} ${pathLength}`;
        basePath.style.strokeDashoffset = '0';
      }
      activeRouteMode = nextMode;
    }

    syncRoutePath();

    function progressAtPathY(targetY) {
      if (targetY <= pathSamples[0].searchY) return 0;
      if (targetY >= pathSamples[pathSamples.length - 1].searchY) return 1;

      let low = 0;
      let high = pathSamples.length - 1;
      while (high - low > 1) {
        const middle = Math.floor((low + high) / 2);
        if (pathSamples[middle].searchY < targetY) low = middle;
        else high = middle;
      }

      const before = pathSamples[low];
      const after = pathSamples[high];
      const span = after.searchY - before.searchY;
      const ratio = span ? clamp((targetY - before.searchY) / span) : 0;
      return before.progress + (after.progress - before.progress) * ratio;
    }

    function screenLengthAtProgress(progress) {
      const samplePosition = clamp(progress) * (pathSamples.length - 1);
      const beforeIndex = Math.floor(samplePosition);
      const afterIndex = Math.min(beforeIndex + 1, pathSamples.length - 1);
      const ratio = samplePosition - beforeIndex;
      const before = pathSamples[beforeIndex].screenLength;
      const after = pathSamples[afterIndex].screenLength;
      return before + (after - before) * ratio;
    }

    rangeNumbers.forEach((number) => {
      number.textContent = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? number.dataset.routeRangeValue
        : '0';
    });

    function animateRangeValues() {
      if (section.dataset.rangeValuesAnimated) return;
      section.dataset.rangeValuesAnimated = 'true';
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const duration = 1100;
      const startTime = performance.now();

      function count(time) {
        const progress = reducedMotion ? 1 : clamp((time - startTime) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        rangeNumbers.forEach((number) => {
          const rawValue = number.dataset.routeRangeValue || '0';
          const target = Number.parseFloat(rawValue) || 0;
          const decimals = (rawValue.split('.')[1] || '').length;
          number.textContent = (target * eased).toFixed(decimals);
        });
        if (progress < 1) requestAnimationFrame(count);
      }

      requestAnimationFrame(count);
    }

    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        if (entry.target.matches('[data-route-range]')) animateRangeValues();
        revealObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -15% 0px', threshold: 0.08 });

    revealTargets.forEach((target) => revealObserver.observe(target));

    const mediaWarmupObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      mediaElements.forEach((media) => {
        if (media.tagName === 'IMG') {
          media.loading = 'eager';
          if (media.decode) media.decode().catch(() => {});
        } else {
          media.preload = 'auto';
          if (media.readyState < 2) media.load();
        }
      });
      mediaWarmupObserver.disconnect();
    }, { rootMargin: '100% 0px', threshold: 0 });
    mediaWarmupObserver.observe(section);

    revealTargets.forEach((target) => {
      const clearWillChange = (event) => {
        if (event.propertyName !== 'transform') return;
        target.style.willChange = 'auto';
        target.removeEventListener('transitionend', clearWillChange);
      };
      target.addEventListener('transitionend', clearWillChange);
    });

    function measure() {
      const map = path.ownerSVGElement;
      const mapRect = map.getBoundingClientRect();
      const sectionRect = section.getBoundingClientRect();
      viewport = window.innerHeight || document.documentElement.clientHeight || 1;
      mapDocumentTop = window.scrollY + mapRect.top;
      mapHeight = mapRect.height || 1;
      mapScaleX = mapRect.width / 1586;
      mapScaleY = mapRect.height / 2550;
      mapOffsetX = mapRect.left - sectionRect.left;
      mapOffsetY = mapRect.top - sectionRect.top;
      markerMatrix = map.getScreenCTM();
      markerOffsetX = markerMatrix ? markerMatrix.e - sectionRect.left : mapOffsetX;
      markerOffsetY = markerMatrix ? markerMatrix.f - sectionRect.top : mapOffsetY;
      screenPathLength = 0;
      pathSamples[0].screenLength = 0;
      for (let index = 1; index < pathSamples.length; index += 1) {
        const before = pathSamples[index - 1];
        const current = pathSamples[index];
        const deltaX = current.x - before.x;
        const deltaY = current.y - before.y;
        const screenDeltaX = markerMatrix ? deltaX * markerMatrix.a + deltaY * markerMatrix.c : deltaX * mapScaleX;
        const screenDeltaY = markerMatrix ? deltaX * markerMatrix.b + deltaY * markerMatrix.d : deltaY * mapScaleY;
        screenPathLength += Math.hypot(screenDeltaX, screenDeltaY);
        current.screenLength = screenPathLength;
      }
      const dashLength = mobileQuery.matches ? screenPathLength : pathLength;
      path.style.strokeDasharray = `${dashLength} ${dashLength}`;
    }

    function readProgress() {
      const viewportGuide = window.scrollY + viewport * .52;
      const endProgress = mobileQuery.matches ? 1 : zeroPoint;
      if (mobileQuery.matches) {
        const targetPathY = (viewportGuide - mapDocumentTop) / mapScaleY;
        targetProgress = clamp(progressAtPathY(targetPathY), 0, endProgress);
      } else {
        targetProgress = clamp((viewportGuide - mapDocumentTop) / mapHeight, 0, endProgress);
      }
    }

    function render(time) {
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const endProgress = mobileQuery.matches ? 1 : zeroPoint;
      const deltaSeconds = previousTime ? Math.min((time - previousTime) / 1000, .05) : 1 / 60;
      previousTime = time;
      readProgress();
      if (reducedMotion) {
        smoothProgress = targetProgress;
        progressVelocity = 0;
      } else {
        const spring = 170;
        const damping = 26;
        const acceleration = spring * (targetProgress - smoothProgress) - damping * progressVelocity;
        progressVelocity += acceleration * deltaSeconds;
        smoothProgress = clamp(smoothProgress + progressVelocity * deltaSeconds, 0, endProgress);
      }
      if (Math.abs(targetProgress - smoothProgress) < .00006 && Math.abs(progressVelocity) < .0005) {
        smoothProgress = targetProgress;
        progressVelocity = 0;
      }

      const isComplete = targetProgress >= endProgress - .0001;
      if (isComplete) {
        smoothProgress = endProgress;
        progressVelocity = 0;
      }

      const drawnLength = pathLength * smoothProgress;
      const dashLength = mobileQuery.matches ? screenPathLength : pathLength;
      const drawnDashLength = mobileQuery.matches ? screenLengthAtProgress(smoothProgress) : drawnLength;
      path.style.strokeDashoffset = `${dashLength - drawnDashLength}`;
      const point = path.getPointAtLength(drawnLength);
      const markerX = markerMatrix
        ? markerOffsetX + point.x * markerMatrix.a + point.y * markerMatrix.c
        : mapOffsetX + point.x * mapScaleX;
      const markerY = markerMatrix
        ? markerOffsetY + point.x * markerMatrix.b + point.y * markerMatrix.d
        : mapOffsetY + point.y * mapScaleY;
      marker.style.translate = `${markerX}px ${markerY}px`;
      marker.classList.toggle('is-visible', smoothProgress > .008 && !isComplete);
      routeDot.classList.toggle('is-visible', smoothProgress > .008 && !isComplete);

      const displayProgress = clamp(smoothProgress / endProgress);
      const hue = 128 * (1 - displayProgress);
      const progressColor = `hsl(${hue} 88% 57%)`;
      marker.style.color = progressColor;
      routeDot.style.setProperty('--dot-color', progressColor);
      const nextValue = String(Math.round(startValue + (endValue - startValue) * displayProgress));
      const nextMiles = (startMiles + (endMiles - startMiles) * displayProgress).toFixed(2);
      const nextKm = (startKm + (endKm - startKm) * displayProgress).toFixed(2);
      if (nextValue !== renderedValue) {
        valueElement.textContent = nextValue;
        renderedValue = nextValue;
      }
      if (nextMiles !== renderedMiles) {
        milesElement.textContent = nextMiles;
        renderedMiles = nextMiles;
      }
      if (nextKm !== renderedKm) {
        kmElement.textContent = nextKm;
        renderedKm = nextKm;
      }

      if (time < activeUntil || Math.abs(smoothProgress - targetProgress) > .00006 || Math.abs(progressVelocity) > .0005) {
        frame = requestAnimationFrame(render);
      } else {
        frame = 0;
        previousTime = 0;
      }
    }

    function requestUpdate() {
      activeUntil = performance.now() + 140;
      if (!frame) frame = requestAnimationFrame(render);
    }

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', () => {
      syncRoutePath();
      measure();
      requestUpdate();
    });
    measure();
    readProgress();
    smoothProgress = targetProgress;
    requestUpdate();
  }

  document.querySelectorAll('[data-scroll-route-section]').forEach(init);
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.matches('[data-scroll-route-section]') ? event.target : event.target.querySelector('[data-scroll-route-section]');
    if (section) init(section);
  });
})();
